import { Check, RotateCcw } from "lucide-react";
import { useState, type JSX, type KeyboardEvent } from "react";
import { useTranslation } from "react-i18next";

import type { components } from "@/api/schema";
import { cn } from "@/shared/lib/cn";
import { toast } from "@/shared/lib/toast";

import { useDeleteRatio } from "../api/use-delete-ratio";
import { useSetRatio } from "../api/use-set-ratio";
import { RatioPolicySchema, type RatioFormValues } from "../lib/ratio-schema";

type RatioPolicy = components["schemas"]["RatioPolicy"];

interface RatioEditorRowProps {
  policy: RatioPolicy;
  readOnly?: boolean;
}

const FIELDS = [
  { key: "ratio_shopee", short: "shopee", labelKey: "ratio.shopee" },
  { key: "ratio_lazada", short: "lazada", labelKey: "ratio.lazada" },
  { key: "ratio_tiktok", short: "tiktok", labelKey: "ratio.tiktok" },
] as const;

export function RatioEditorRow({
  policy,
  readOnly = false,
}: RatioEditorRowProps): JSX.Element | null {
  const { t } = useTranslation("inventory");
  const sku = policy.sku ?? "";
  const setRatio = useSetRatio();
  const deleteRatio = useDeleteRatio();

  const [values, setValues] = useState<RatioFormValues>({
    ratio_shopee: policy.ratio_shopee ?? 40,
    ratio_lazada: policy.ratio_lazada ?? 40,
    ratio_tiktok: policy.ratio_tiktok ?? 20,
  });

  if (!sku) return null;

  const sum = values.ratio_shopee + values.ratio_lazada + values.ratio_tiktok;
  const parsed = RatioPolicySchema.safeParse(values);
  const isBusy = setRatio.isPending || deleteRatio.isPending;
  const canSave = parsed.success && !isBusy;

  function handleChange(key: keyof RatioFormValues, raw: string) {
    const n = raw === "" ? 0 : Number(raw);
    setValues((prev) => ({ ...prev, [key]: Number.isFinite(n) ? n : 0 }));
  }

  function handleSave() {
    if (!parsed.success) return;
    setRatio.mutate(
      { sku, body: parsed.data },
      {
        onSuccess: () => toast.success(t("ratio.toast.saved")),
        onError: () => toast.error(t("ratio.toast.save_error")),
      },
    );
  }

  function handleReset() {
    deleteRatio.mutate(
      { sku },
      {
        onSuccess: () => {
          setValues({ ratio_shopee: 40, ratio_lazada: 40, ratio_tiktok: 20 });
          toast.success(t("ratio.toast.reverted"));
        },
        onError: () => toast.error(t("ratio.toast.save_error")),
      },
    );
  }

  function handleKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSave();
    }
  }

  return (
    <tr
      data-testid={`ratio-row-${sku}`}
      className="h-[52px] transition-colors hover:bg-[var(--row-hover)]"
    >
      <td className="sku px-4 text-sm font-semibold text-[var(--text-primary)]">{sku}</td>
      {FIELDS.map((f) => (
        <td key={f.key} className="px-4">
          {readOnly ? (
            <span
              className="num block text-right tabular-nums"
              data-testid={`ratio-value-${f.short}-${sku}`}
            >
              {values[f.key]}
            </span>
          ) : (
            <input
              type="number"
              min={0}
              max={100}
              step={1}
              value={String(values[f.key])}
              onChange={(e) => handleChange(f.key, e.target.value)}
              onKeyDown={handleKeyDown}
              aria-label={t(f.labelKey)}
              data-testid={`ratio-input-${f.short}-${sku}`}
              className="num h-9 w-[72px] rounded-[var(--control-r)] border border-[var(--premium-border-strong)] bg-white px-3 text-right text-sm tabular-nums shadow-[var(--sh-xs)] outline-none transition focus:border-[var(--brand-500)] focus-ring"
            />
          )}
        </td>
      ))}
      <td className="px-4">
        <span
          data-testid={`ratio-sum-indicator-${sku}`}
          data-status={sum === 100 ? "ok" : "error"}
          className={cn(
            "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-bold tabular-nums",
            sum === 100
              ? "bg-[var(--intent-success-bg)] text-[var(--intent-success-fg)]"
              : "bg-[var(--intent-danger-bg)] text-[var(--intent-danger-fg)]",
          )}
        >
          {sum}
        </span>
      </td>
      <td className="px-4">
        {!readOnly && (
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              onClick={handleSave}
              disabled={!canSave}
              data-testid={`ratio-save-${sku}`}
              aria-label={t("ratio.action.save")}
              className="inline-flex h-9 items-center gap-1.5 rounded-[var(--control-r)] bg-[var(--brand-500)] px-3 text-[13px] font-semibold text-white shadow-[var(--sh-xs)] transition hover:bg-[var(--brand-600)] disabled:cursor-not-allowed disabled:opacity-50 focus-ring"
            >
              <Check className="h-[15px] w-[15px]" aria-hidden />
              {t("ratio.action.save")}
            </button>
            {policy.is_explicit && (
              <button
                type="button"
                onClick={handleReset}
                disabled={deleteRatio.isPending}
                data-testid={`ratio-delete-${sku}`}
                aria-label={t("ratio.action.reset")}
                className="inline-flex h-9 items-center gap-1.5 rounded-[var(--control-r)] px-3 text-[13px] font-medium text-[var(--text-subtle)] transition hover:bg-[var(--canvas)] disabled:opacity-50 focus-ring"
              >
                <RotateCcw className="h-[15px] w-[15px]" aria-hidden />
                {t("ratio.action.reset")}
              </button>
            )}
          </div>
        )}
      </td>
    </tr>
  );
}
